import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { Box, Button, ButtonGroup, Container, FormControl, InputAdornment, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import { useHistory } from "react-router-dom";
import ConfirmationModal from '../../components/ConfirmationModal';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Iconify from '../../components/Iconify';
import KanbanColumn from '../../components/KanbanColumn';
import KanbanFiltersDrawer from '../../components/KanbanFiltersDrawer';
import KanbanInput from '../../components/KanbanInput';
import KanbanSkeleton from '../../components/KanbanSkeleton';
import Page from '../../components/Page';
import socket from '../../hooks/useSocket';
import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { hideScrollbarX } from '../../utils/cssStyles';
import { AuthContext } from '../../context/Auth/AuthContext';
import { useEvent } from '../../hooks/useEvent';
import ToastSuccess from '../../toast/success/toastSuccess';
import KanbanCardDrawer from '../../components/KanbanCardDrawer';
import KanbanTagsAutoComplete from '../../components/KanbanTagsAutoComplete';
import CreateOrEditKanbanTagModal from '../../components/CreateOrEditKanbanTagModal';
import KanbanUsersAutoComplete from '../../components/KanbanUsersAutoComplete';
import KanbanPriorityAutoComplete from '../../components/KanbanPriorityAutoComplete';
import CreateOrEditKanbanPriorityModal from '../../components/CreateOrEditKanbanPriorityModal';
import ContactFilterAutoComplete from '../../components/ContactFilterAutoComplete';

const initialLoading = { board: false, priority: false, users: false, listBoard: false, tag: false };

export default function KanbanBoard() {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const emitter = useEvent();
  const [kanban, setKanban] = useState(null);
  const [kanbanFilter, setKanbanFilter] = useState(null);
  const [addingColumn, setAddingColumn] = useState(false);
  const [kanbanList, setKanbanList] = useState([]);
  const { kanbanId } = useParams();
  const [loading, setLoading] = useState(initialLoading);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [typeActionModal, setTypeActionModal] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [priorities, setPriorities] = useState([]);
  const [cardTags, setCardTags] = useState([]);
  const [filters, setFilters] = useState(null);
  const [searchParam, setSearchParam] = useState(null);
  const [focusedSearch, setFocusedSearch] = useState(false);
  const [editCardOpen, setEditCardOpen] = useState(false);
  const [editCardId, setEditCardId] = useState(false);

  const [selectedTags, setSelectedTags] = useState([]);
  const [openTagModal, setOpenTagModal] = useState(false);

  const [assignedFilter, setAssignedFilter] = useState([]);

  const [priorityFilter, setPriorityFilter] = useState(null);
  const [openPriorityModal, setOpenPriorityModal] = useState(false);

  const [requester, setRequester] = useState(null);

  useEffect(() => {
    const filtersData = { ...filters, selectedTags, assignedFilter, priorityFilter }
    setFilters(filtersData);
    setTimeout(() => {
      localStorage.setItem(`kanbanFilters_${kanban?.id}`, JSON.stringify(filtersData));
    }, 500);
  }, [selectedTags, assignedFilter, priorityFilter]);

  useEffect(() => {
    if (!kanbanId) return;

    // Função para buscar prioridades
    (async () => {
      try {
        setLoading((prev) => ({ ...prev, priority: true }));
        const { data } = await api.get(`/kanban/priority/${kanbanId}`);
        setPriorities(data);
      } catch (err) {
        console.error("Erro ao buscar prioridades:", err);
      } finally {
        setLoading((prev) => ({ ...prev, priority: false }));
      }
    })();

    // Função para buscar tags
    (async () => {
      try {
        setLoading((prev) => ({ ...prev, tag: true }));
        const { data } = await api.get(`/kanban/tag/${kanbanId}`);
        setCardTags(data);
      } catch (err) {
        console.error("Erro ao buscar tags:", err);
      } finally {
        setLoading((prev) => ({ ...prev, tag: false }));
      }
    })();

    // Função para buscar o board
    (async () => {
      try {
        setLoading((prev) => ({ ...prev, board: true }));
        const { data } = await api.get(`/kanban/board/${kanbanId}`);
        setKanban(data);
      } catch (err) {
        console.error("Erro ao buscar board:", err);
      } finally {
        setLoading((prev) => ({ ...prev, board: false }));
      }
    })();

    // Função para buscar a lista de kanbans
    (async () => {
      try {
        setLoading((prev) => ({ ...prev, listBoard: true }));
        const { data } = await api.get("/kanbans/list");
        setKanbanList(data);
      } catch (err) {
        console.error("Erro ao buscar lista de kanbans:", err);
      } finally {
        setLoading((prev) => ({ ...prev, listBoard: false }));
      }
    })();

  }, [kanbanId]);

  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    if (!destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index)) {
      return;
    }

    const handlers = {
      COLUMN: handleChangeColumnPosition,
      CARD: handleChangeCardPosition
    };

    const handler = handlers[type] || handlers.CARD;
    handler(result);
  };

  const handleChangeCardPosition = async (result) => {
    const { source, destination, draggableId: cardId } = result;
    // Evita operações desnecessárias quando não há mudança real
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

    try {
      const { data } = await api.put(`/kanban/card/${cardId}`, {
        order: destination.index,
        columnId: destination.droppableId
      })

      let isMoveColumn = source?.droppableId !== destination?.droppableId;
      let isMoveOrder = source?.index !== destination?.index;
      let oldColumnId = source.droppableId;

      emitter.emit("kanban-card", {
        action: isMoveColumn ? "column" : isMoveOrder ? "order" : "update",
        ...(isMoveColumn && { oldColumnId }),
        kanbanId: kanbanId,
        cardId: data.id,
        columnId: data.kanbanColumnId,
        card: data
      });

    } catch (error) {
      console.error(error)
    } finally {

    }
  };

  const handleChangeColumnPosition = async ({ source, destination, draggableId }) => {
    if (!destination || source.index === destination.index) return;
    const newPosition = destination.index;
    try {
      const { data } = await api.put(`/kanban/column/${draggableId}`, { position: newPosition });

      let isMoveColumn = source.index !== destination.index;
      let oldPosition = source.index;

      emitter.emit("kanban-column", {
        action: isMoveColumn ? "position" : "update",
        ...(isMoveColumn && { oldPosition }),
        kanbanId: kanbanId,
        columnId: data.id,
        column: data
      });

    } catch (error) {
      console.error(error);
    }
  };

  const createCard = async (columnId, card) => {
    try {
      const { data } = await api.post(`/kanban/card/${columnId}`, { title: card.title, description: '' });
      emitter.emit("kanban-card", { action: 'create', kanbanId, columnId, card: data });

      setEditCardId(data.id);
      setTimeout(() => setEditCardOpen(true), 500);
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const handleCreateColumn = async (inputVal) => {
    try {
      const { data } = await api.post(`/kanban/column/${kanban.id}`, { title: inputVal });
      emitter.emit("kanban-column", { action: 'create', kanbanId, column: data });
    } catch (error) {
      console.error(error);
    } finally {
      setAddingColumn(false);
    }
  };

  const handleDeleteColumn = async (columnId) => {
    try {
      await api.delete(`/kanban/column/${columnId}`, {
        params: { action: 'archive' },
      });
      emitter.emit("kanban-column", { action: 'delete', kanbanId, columnId });
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const handleCleanColumn = async (columnId) => {
    try {
      await api.delete(`/kanban/column/${columnId}`, {
        params: { action: 'clean' },
      });
    } catch (error) {
      console.error(error);
    } finally {
      // Ações no final, se necessário
    }
  };

  const renameColumn = async (column) => {
    const oldKanbanData = { ...kanban };
    try {
      await api.put(`/kanban/column/${column.id}`, { title: column.title });
      emitter.emit("kanban-column", { action: 'update', kanbanId, columnId: column.id, column });
    } catch (error) {
      setKanban(oldKanbanData);
      console.error('Erro ao renomear a coluna:', error);
    }
  };

  // Functions to events from socket and emitter

  const kanban_card_event = (data) => {
    if (data?.kanbanId === kanbanId) {
      if (data?.userSocket === user?.id) return;
      try {


        if (data.action === 'create') {
          setKanban((prevState) => {
            const updatedColumns = prevState.column.map((col) => {
              if (col.id === data.columnId) {
                // Ensure col.cards is an array
                const colCards = Array.isArray(col.cards) ? col.cards : [];
                const newCards = [data.card, ...colCards];
                const updatedCards = newCards.map((card, index) => ({
                  ...card,
                  order: index,
                }));

                return { ...col, cards: updatedCards };
              }
              return col;
            });
            return {
              ...prevState,
              column: updatedColumns,
            };
          });
        }

        if (data.action === 'update') {
          setKanban((prevData) => {
            const updatedColumns = prevData.column.map((c) => {
              // Ensure c.cards is an array
              const cards = Array.isArray(c.cards) ? c.cards : [];
              const cardIndex = cards.findIndex((card) => card.id === data.cardId);
              if (cardIndex !== -1) {
                const updatedCards = [...cards];
                updatedCards[cardIndex] = { ...updatedCards[cardIndex], ...data.card };
                return { ...c, cards: updatedCards };
              }
              return c;
            });
            return { ...prevData, column: updatedColumns };
          });
        }

        if (data.action === 'order') {
          setKanban((prevState) => {
            const updatedColumns = prevState.column.map((c) => {
              if (c.id === data.columnId) {
                // Ensure c.cards is an array
                const cards = Array.isArray(c.cards) ? c.cards : [];
                const cardIndex = cards.findIndex((card) => card.id === data.cardId);

                if (cardIndex === -1) return c; // Card not found

                const currentCards = [...cards];
                const removedCard = currentCards.splice(cardIndex, 1);
                const newPosition = data.card.order;
                currentCards.splice(newPosition, 0, removedCard[0]);
                const updatedCards = currentCards.map((card, index) => ({
                  ...card,
                  order: index,
                }));
                return { ...c, cards: updatedCards };
              }
              return c;
            });
            return {
              ...prevState,
              column: updatedColumns,
            };
          });
        }

        if (data.action === 'column') {
          setKanban((prevState) => {
            const updatedColumns = prevState.column.map((col) => {
              const isSource = col.id === data.oldColumnId;
              if (isSource) {
                // Ensure col.cards is an array
                const cards = Array.isArray(col.cards) ? col.cards : [];
                const cardIndex = cards.findIndex((card) => card.id === data.cardId);

                if (cardIndex > -1) {
                  const currentCards = [...cards];
                  currentCards.splice(cardIndex, 1);
                  const updatedCards = currentCards.map((card, index) => ({
                    ...card,
                    order: index,
                  }));

                  return {
                    ...col,
                    cards: updatedCards,
                  };
                }
              }
              return col;
            });
            return { ...prevState, column: updatedColumns };
          });

          setKanban((prevState) => {
            const updatedColumns = prevState.column.map((col) => {
              const isDestination = col.id === data.columnId;
              if (isDestination) {
                // Ensure col.cards is an array
                const cards = Array.isArray(col.cards) ? col.cards : [];

                const cardExists = cards.some((card) => card.id === data.card.id);
                if (cardExists) {
                  return col;
                }

                const destinationCards = [...cards];
                const newPosition = data.card.order;
                destinationCards.splice(newPosition, 0, data.card);
                const updatedCards = destinationCards.map((card, index) => ({
                  ...card,
                  order: index,
                }));
                return {
                  ...col,
                  cards: updatedCards,
                };
              }
              return col;
            });
            return { ...prevState, column: updatedColumns };
          });
        }

        if (data.action === 'delete') {
          setKanban((prevData) => {
            const updatedColumns = prevData.column.map((column) => {
              // Ensure column.cards is an array
              const cards = Array.isArray(column.cards) ? column.cards : [];
              const updatedCards = cards.filter((card) => card.id !== data.cardId);
              return { ...column, cards: updatedCards };
            });
            return { ...prevData, column: updatedColumns };
          });
        }
      } catch (error) {
        console.info(error)
      }
    }
  };

  const kanban_assigneds_event = (data) => {
    if (data?.userSocket === user?.id) return;
    setKanban((prevData) => {
      const updatedColumns = prevData.column.map((column) => {
        const cardIndex = column.cards.findIndex((card) => card.id === data.cardId);
        if (cardIndex !== -1) {
          const updatedCards = [...column.cards];
          updatedCards[cardIndex] = { ...updatedCards[cardIndex], assigned: data.assigneds };
          return { ...column, cards: updatedCards };
        }
        return column;
      });
      return { ...prevData, column: updatedColumns };
    });
  };

  const kanban_comments_event = (data) => {
    if (data?.userSocket === user?.id) return;
    console.info({ data })
    if (data.action === 'create') {
      setKanban((prevData) => {
        const updatedColumns = prevData.column.map((column) => {
          const updatedCards = column.cards.map((card) =>
            card.id === data.cardId
              ? { ...card, comments: [...(card.comments || []), data.comment] }
              : card
          );
          return { ...column, cards: updatedCards };
        });
        return { ...prevData, column: updatedColumns };
      });

    } else {
      setKanban((prevData) => {
        const updatedColumns = prevData.column.map((column) => {
          const cardIndex = column.cards.findIndex((card) => card.id === data.cardId);
          if (cardIndex !== -1) {
            const updatedCards = [...column.cards];
            updatedCards[cardIndex] = { ...updatedCards[cardIndex], comments: data.comments };
            return { ...column, cards: updatedCards };
          }
          return column;
        });
        return { ...prevData, column: updatedColumns };
      });
    }
  };

  const kanban_task_event = (data) => {
    if (data?.userSocket === user?.id) return;
    setKanban((prevData) => {
      const updatedColumns = prevData.column.map((column) => {
        const cardIndex = column.cards.findIndex((card) => card.id === data.cardId);
        if (cardIndex !== -1) {
          const updatedCards = [...column.cards];
          updatedCards[cardIndex] = { ...updatedCards[cardIndex], tasks: data.tasks };
          return { ...column, cards: updatedCards };
        }
        return column;
      });
      return { ...prevData, column: updatedColumns };
    });
  };

  const kanban_task_checklist_event = (data) => {
    if (data?.userSocket === user?.id) return;
    setKanban((prevData) => {
      const updatedColumns = prevData.column.map((column) => {
        const cardIndex = column.cards.findIndex((card) => card.id === data.cardId);
        if (cardIndex !== -1) {
          const updatedCards = [...column.cards];
          const tasks = updatedCards[cardIndex].tasks || [];
          const taskIndex = tasks.findIndex((task) => task.id === data.taskId);
          if (taskIndex !== -1) {
            const updatedTasks = [...tasks];
            updatedTasks[taskIndex] = {
              ...updatedTasks[taskIndex],
              checklists: data.checklists,
            };
            updatedCards[cardIndex] = {
              ...updatedCards[cardIndex],
              tasks: updatedTasks,
            };
            return { ...column, cards: updatedCards };
          }
        }
        return column;
      });
      return { ...prevData, column: updatedColumns };
    });
  };

  const kanban_column_event = (data) => {
    if (data?.kanbanId === kanbanId) {
      if (data?.userSocket === user?.id) return;
      if (data.action === 'position') {
        const newPosition = data.column.position;
        setKanban((prevState) => {
          const columns = [...prevState.column];
          const columnIndex = columns.findIndex((col) => col.id === data.columnId);
          const [movedColumn] = columns.splice(columnIndex, 1);
          columns.splice(newPosition, 0, { ...movedColumn, position: newPosition });
          const reorderedColumns = columns.map((col, index) => ({ ...col, position: index }));
          return { ...prevState, column: reorderedColumns };
        });
      }
      if (data.action === 'update') {
        setKanban((prevState) => {
          const columnExists = prevState.column.some((col) => col.id === data.columnId);
          return {
            ...prevState,
            column: columnExists
              ? prevState.column.map((col) =>
                col.id === data.columnId ? { ...col, ...data.column } : col
              )
              : [...prevState.column, { id: data.columnId, ...data.column }],
          };
        });

      }
      if (data.action === 'create') {
        setKanban((prevState) => {
          const columnExists = prevState.column.some((col) => col.id === data.column.id);
          let updatedColumns = [...prevState.column];
          if (columnExists) {
            updatedColumns = updatedColumns.filter((col) => col.id !== data.column.id);
          }
          const newPosition = updatedColumns.length;
          updatedColumns.push({ ...data.column, position: newPosition });
          return { ...prevState, column: updatedColumns };
        });
      }
      if (data.action === 'delete') {
        setKanban((prevState) => {
          const columnExists = prevState.column.some((col) => col.id === data.columnId);
          if (columnExists) {
            const updatedColumns = prevState.column.filter((col) => col.id !== data.columnId);
            return { ...prevState, column: updatedColumns };
          }
          return prevState;
        });
      }
    }
  };

  const kanban_priority_event = (data) => {
    if (data?.userSocket === user?.id) return;
    if (data?.priority?.isGlobal || !data?.priority?.kanbanId === kanbanId) {
      if (data.action === 'create') {
        setPriorities((pre) => [...pre, data.priority]);
      }
      if (data.action === 'update') {
        setPriorities((pre) => pre.map((priority) => (priority.id === data.priority.id ? data.priority : priority)));
      }
    }
  };

  useEffect(() => {
    if (!kanban?.id) return;
    const savedFilters = localStorage.getItem(`kanbanFilters_${kanban?.id}`);
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      setPriorityFilter(parsedFilters?.priorityFilter);
      setAssignedFilter(parsedFilters?.assignedFilter);
      setSelectedTags(parsedFilters?.selectedTags || []);
    }
  }, [kanban]);

  const kanban_tag_event = (data) => {
    if (data?.userSocket === user?.id) return;
    if (data?.tag?.isGlobal || data?.tag?.kanbanId === kanbanId) {
      if (data.action === 'create') {
        setCardTags((pre) => [...pre, data.tag]);
      }
      if (data.action === 'update') {
        setCardTags((pre) => pre.map((tag) => (tag.id === data.tag.id ? data.tag : tag)));
      }
    }
  };

  useEffect(() => {
    // Event emitter
    emitter.on('kanban-card', kanban_card_event);
    emitter.on('kanban-assigneds', kanban_assigneds_event);
    emitter.on('kanban-comments', kanban_comments_event);
    emitter.on('kanban-task', kanban_task_event);
    emitter.on('kanban-task-checklist', kanban_task_checklist_event);
    emitter.on('kanban-column', kanban_column_event);
    emitter.on(`kanban-priority`, kanban_priority_event);
    emitter.on(`kanban-tag`, kanban_tag_event);
    // socket
    socket.on('kanban-card', kanban_card_event);
    socket.on('kanban-assigneds', kanban_assigneds_event);
    socket.on('kanban-comments', kanban_comments_event);
    socket.on('kanban-task', kanban_task_event);
    socket.on('kanban-task-checklist', kanban_task_checklist_event);
    socket.on('kanban-column', kanban_column_event);
    socket.on(`kanban-priority`, kanban_priority_event);
    socket.on(`kanban-tag`, kanban_tag_event);

    return () => {
      // Event emitter
      /*
      emitter.off('kanban-card', kanban_card_event);
      emitter.off('kanban-assigneds', kanban_assigneds_event);
      emitter.off('kanban-comments', kanban_comments_event);
      emitter.off('kanban-task', kanban_task_event);
      emitter.off('kanban-task-checklist', kanban_task_checklist_event);
      emitter.off('kanban-column', kanban_column_event);
      emitter.off(`kanban-priority`, kanban_priority_event);
      emitter.off(`kanban-tag`, kanban_tag_event);
      */
      // socket
      /*
      socket.off('kanban-card');
      socket.off('kanban-assigneds');
      socket.off('kanban-comments');
      socket.off('kanban-task');
      socket.off('kanban-task-checklist');
      socket.off('kanban-column');
      socket.off(`kanban-priority`);
      socket.off(`kanban-tag`);
      */
    };
  }, [kanbanId]);

  useEffect(() => {
    if (!open || !kanban?.id) return;
    const savedFilters = localStorage.getItem(`kanbanFilters_${kanban?.id}`);
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        setFilters(parsedFilters);
      } catch (error) {
        console.error('Erro ao fazer parse dos filtros salvos:', error);
      }
    } else {
      setFilters(null);
    }
  }, [kanban]);

  const handleFilterCards = (newFilters) => {
    setFilters(newFilters);
  };

  const handleCleanFilters = () => {
    setFilters(null);
    localStorage.removeItem(`kanbanFilters_${kanban?.id}`);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  function filterCardsInColumns(data, searchValue) {
    if (!searchValue) return data;

    const lowerCaseSearchValue = searchValue.toLowerCase();

    return {
      ...data,
      column: data.column.map((column) => ({
        ...column,
        cards: column.cards.filter(
          (card) =>
            (card.title && card.title.toLowerCase().includes(lowerCaseSearchValue)) ||
            (card.taskNumber && card.taskNumber.toLowerCase().includes(lowerCaseSearchValue))
        ),
      })),
    };
  }

  useEffect(() => {
    const kanbanFiltered = filterCardsInColumns(kanban, searchParam);
    setKanbanFilter(kanbanFiltered);
  }, [searchParam]);

  useEffect(() => {
    setKanbanFilter(kanban);
  }, [kanban]);

  const columns = kanbanFilter?.column?.sort((a, b) => a.position - b.position) || null;

  const handleChangeBoard = (e) => {
    if (e.target.value) {
      history.push(`/kanban/board/${e.target.value}`)
    }
  };


  const existStartedColumn = columns?.some((column) => column.hasStart);
  const existFinishedColumn = columns?.some((column) => column.hasFinished);

  const handleCreateTag = () => {
    setOpenTagModal(true)
  };

  const handleCreatePriority = () => {
    setOpenPriorityModal(true);
  };


  const updatePriorityValue = (values, setFunction) => {
    if (!values || values.length === 0 || (values.length === 1 && values[0] === undefined)) {
      setFunction([]);
    } else {
      setFunction([values[values.length - 1]]);
    }
  };

  const updateTagsValue = (values, setFunction) => {
    if (!values || values.length === 0 || (values.length === 1 && values[0] === undefined)) {
      setFunction([]);
    } else {
      setFunction(values);
    }
  };

  return (
    <>
      <Box
        component="div"
        sx={{
          marginTop: '5px',
          // background: 'url(https://picsum.photos/1920/1080)',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#161c24' : '#ffffff',
          height: '100%'
        }}>
        <Page title={i18n.t('mainDrawer.listItems.kanban')}>
          <Container maxWidth={false}>
            <HeaderBreadcrumbs
              sx={{ mb: 0, marginTop: '10px' }}
              heading={
                <>
                  <FormControl
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: 120,
                      '& .MuiInputBase-input': {
                        padding: '3.5px 5px',
                      },
                    }}>
                    <Select
                      disabled={loading?.board}
                      inputProps={{ 'aria-label': 'Without label' }}
                      value={loading?.board ? "" : kanbanId || ""}
                      onChange={e => handleChangeBoard(e)}
                      placeholder="Quadro"
                      size="small"
                    >
                      <MenuItem value="" disabled>
                        <em>Todos</em>
                      </MenuItem>
                      {(kanbanList || []).map((item) => <MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>)}
                    </Select>
                  </FormControl>
                </>
              }
              action={
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  <TextField
                    fullWidth
                    sx={{
                      width: focusedSearch ? '500px' : '300px',
                      minWidth: '300px',
                      marginRight: '20px',
                      transition: 'width 0.2s ease',
                      '& .MuiInputBase-input': {
                        padding: '3.5px 5px',
                      },
                    }}
                    size="small"
                    type="search"
                    placeholder={`Buscar Titulo ou N° ${kanban?.taskTitle
                      ? kanban.taskTitle.charAt(0).toUpperCase() + kanban.taskTitle.slice(1)
                      : 'Protocolo'
                      }`}
                    value={searchParam}
                    onChange={handleSearch}
                    onFocus={() => setFocusedSearch(true)}
                    onBlur={() => setFocusedSearch(false)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: 'gray' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {(filters && (
                    !!filters && filters?.showTaskCount !== false ||
                    !!filters && filters?.showTaskNumber !== false ||
                    !!filters && filters?.showTags !== false ||
                    !!filters && filters?.priorityFilter !== null ||
                    !!filters && filters?.assignedFilter.length !== 0 ||
                    !!filters && filters?.createdByFilter.length !== 0 ||
                    !!filters && filters?.startAt !== null ||
                    !!filters && filters?.endAt !== null ||
                    !!filters && filters?.requester !== null ||
                    !!filters && filters?.showCreatedBy !== false ||
                    !!filters && filters?.viewCardPriority !== false
                  )) ? (
                    <ButtonGroup aria-label="Basic button group">
                      <Button
                        variant="outlined"
                        startIcon={<Iconify icon="solar:filter-bold" width={20} />}
                        onClick={() => setOpenFilters(true)}
                        size="small"
                      >
                        Filtros
                      </Button>
                      <Button
                        variant="contained"
                        endIcon={<Iconify icon="ic:round-clear" width={20} />}
                        onClick={() => handleCleanFilters()}
                        size="small"
                      >
                        Remover
                      </Button>
                    </ButtonGroup>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<Iconify icon="solar:filter-bold" width={20} />}
                      variant="outlined"
                      onClick={() => setOpenFilters(true)}
                    >
                      Filtros
                    </Button>
                  )}
                </Box>
              }
            />
            <Box
              display="flex"
              flexWrap="wrap"
              gap="5px"
              sx={{ "& > *": { flex: "1 1 calc(33.333% - 5px)" } }}
            >
              <KanbanTagsAutoComplete
                labels={cardTags}
                placeholder="Marcadores"
                search={"Marcadores de Tarefa"}
                setValue={(values) => updateTagsValue(values, setSelectedTags)}
                value={selectedTags}
                loading={loading.tag}
                handleCreate={handleCreateTag}
              />
              <KanbanPriorityAutoComplete
                labels={priorities || []}
                loading={loading.priority}
                placeholder="Prioridade"
                search={"Filtrar por prioridade"}
                setValue={(values) => updatePriorityValue(values, setPriorityFilter)}
                value={priorityFilter}
                handleCreate={handleCreatePriority}
              />
              <KanbanUsersAutoComplete
                labels={kanban?.assigneds || []}
                placeholder="Atribuidos"
                search={"Filtrar atribuidos"}
                setValue={setAssignedFilter}
                value={assignedFilter || []}
                loading={loading.users}
              />
            </Box>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="all-columns" direction="horizontal" type="COLUMN">
                {(provided) => (
                  <Stack
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    spacing={2}
                    direction="row"
                    alignItems="flex-start"
                    sx={{
                      height: 'calc(100vh - 200px)',
                      overflowX: 'auto',
                      overflowY: 'auto',
                      padding: '10px 0',
                      width: '100%',
                    }}
                  >
                    {loading.board ? (
                      <KanbanSkeleton />
                    ) : columns?.length > 0 ? (
                      columns.map((column, index) => {
                        return (
                          <KanbanColumn
                            kanban={kanban}
                            key={column.id}
                            index={index}
                            filters={filters}
                            taskTitle={kanban?.taskTitle || 'protocolo'}
                            column={column}
                            assigneds={kanban?.assigneds}
                            priorities={priorities}
                            cardTags={cardTags}
                            cards={column?.cards || []}
                            createCard={createCard}
                            clearColumn={() => {
                              setTypeActionModal({ action: 'clean', columnId: column.id, title: column.title });
                              setOpenConfirmationModal(true);
                            }}
                            deleteColumn={() => {
                              setTypeActionModal({ action: 'archive', columnId: column.id, title: column.title });
                              setOpenConfirmationModal(true);
                            }}
                            renameColumn={renameColumn}
                          />
                        );
                      })
                    ) : null}
                    {provided.placeholder}
                    <Box sx={{ ml: 2, mt: 1, minWidth: 280 }}>
                      {addingColumn ? (
                        <KanbanInput
                          label="Titulo"
                          limit={25}
                          onSave={handleCreateColumn}
                          onCancel={() => {
                            console.info('outsider');
                            setAddingColumn(false);
                          }}
                        />
                      ) : (
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddingColumn(true);
                          }}
                          variant="outlined"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            width: '100%',
                            border: '1px dashed',
                            borderColor: (theme) => theme.palette.primary.main,
                            color: (theme) => theme.palette.primary.main,
                            '&:hover': {
                              color: '#fff',
                              borderColor: (theme) => theme.palette.primary.main,
                              backgroundColor: (theme) => theme.palette.primary.main,
                            },
                          }}
                        >
                          <Iconify icon="carbon:add" width={20} />
                          <Box component="span" sx={{ ml: 1 }}>
                            Nova Coluna
                          </Box>
                        </Button>
                      )}
                    </Box>
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </Container>
        </Page>
        <KanbanFiltersDrawer
          existStartedColumn={existStartedColumn}
          existFinishedColumn={existFinishedColumn}
          taskTitle={kanban?.taskTitle || 'protocolo'}
          priorities={priorities}
          cardTags={cardTags}
          loadingPriority={loading.priority}
          loadingTag={loading.tag}
          open={openFilters}
          onClose={setOpenFilters}
          kanban={kanban}
          onFilter={handleFilterCards}
        />
        <KanbanCardDrawer
          priorities={priorities}
          cardTags={cardTags}
          assigneds={kanban?.assigneds}
          open={editCardOpen}
          onClose={() => {
            setEditCardOpen(false);
            setEditCardId(null);
          }}
          cardId={editCardId}
        />
        <CreateOrEditKanbanTagModal
          open={openTagModal}
          onClose={setOpenTagModal}
          kanbanId={kanban?.id}
        />
        <CreateOrEditKanbanPriorityModal
          open={openPriorityModal}
          onClose={setOpenPriorityModal}
          kanbanId={kanban?.id}
        />

        <ConfirmationModal
          title={
            typeActionModal?.action === 'clean'
              ? `Limpar a coluna "${typeActionModal?.title}"?`
              : `Arquivar a coluna "${typeActionModal?.title}"?`
          }
          open={openConfirmationModal}
          onClose={() => {
            setOpenConfirmationModal(false);
            setTypeActionModal(null);
          }}
          onConfirm={async () =>
            typeActionModal?.action === 'clean'
              ? await handleCleanColumn(typeActionModal?.columnId)
              : await handleDeleteColumn(typeActionModal?.columnId)
          }
        >
          {typeActionModal?.action === 'clean'
            ? 'Você tem certeza de que deseja limpar esta coluna? Esta ação não pode ser desfeita, e todas as tarefas vinculadas serão permanentemente removidas.'
            : 'Você tem certeza de que deseja archivar esta coluna?  todas as tarefas vinculadas serão permanentemente na coluna que foi arquivada.'}
        </ConfirmationModal>
      </Box>
    </>
  );
}
